import type React from "react";
import { motion } from "framer-motion";
import house from "../assets/images/house.jpg";
import { Divider, Fleuron } from "./Ornament";
import { easeManor } from "../constants/constants";

type Chapter = {
  year: string;
  heading: string;
  body: string[];
  aside?: React.ReactNode;
};

const chapters: Chapter[] = [
  {
    year: "1915",
    heading: "A parcel on Broadway.",
    body: [
      "On the eighteenth of October, 1915, George Washington Brown and his wife, Effie Charlotte Forehand Brown, purchased a plot of Beaumont land from W.P.H. McFaddin for three hundred and sixty dollars. Eleven years later, on that same patch of Broadway, the manor took its first shape — built to welcome company.",
    ],
  },
  {
    year: "1926 – 1956",
    heading: "The Brown years.",
    body: [
      "From the first season the house found its rhythm. Effie hosted Needlecraft Club meetings, Bridge parties, and teas for the Daughters of the American Revolution; a January morning in 1931 was spent placing a newspaper advertisement after the family Airedale, Stub, went missing from the yard — a reward was offered.",
      "Their son, George Jr., was admitted to federal practice in 1938, served as a Lieutenant Commander in the Navy during the war, and married in Genoa in the autumn of 1948. His mother had only weeks to meet her new daughter-in-law at a welcoming tea that November before she passed. George Sr., a founding member of the Beaumont Country Club and long a presence in the city's oil, land, and banking circles, followed in the autumn of 1956.",
    ],
    aside: (
      <div className="grid grid-cols-2 gap-5 mt-10">
        <figure className="frame-gilt rotate-[-1deg]">
          <img
            src="/assets/portraits/effie_brown.jpeg"
            alt="Effie Charlotte Forehand Brown"
            className="w-full h-80 object-cover"
          />
        </figure>
        <figure className="frame-gilt rotate-[1deg]">
          <img
            src="/assets/portraits/george_brown_sr.jpeg"
            alt="George Washington Brown, Sr."
            className="w-full h-80 object-cover"
          />
        </figure>
        <figcaption className="u-label text-center">
          Effie C. F. Brown
        </figcaption>
        <figcaption className="u-label text-center">
          George W. Brown, Sr.
        </figcaption>
      </div>
    ),
  },
  {
    year: "1966 – 1995",
    heading: "An episcopal residence.",
    body: [
      "The Diocese of Beaumont was established on the twenty-ninth of September, 1966. The following month, George Brown Jr. gifted the residence to its first bishop, Vincent Harris — and in the years that followed, four bishops in turn called the manor home.",
      "Bishop Harris served until his transfer to the Diocese of Austin in 1971. Bishop Warren Boudreaux followed, remaining until 1977. Bishop Bernard Ganter lived in the house from 1977 until his death in 1993. Bishop Joseph Galante was appointed in 1994 and was the last of the four; the property was sold in the May of 1995.",
    ],
  },
  {
    year: "1995 – 2024",
    heading: "Chambers and ledgers.",
    body: [
      "For three decades the house served as offices for a succession of firms — Cashiola Law Firm, Templeton & Brinkley, Alicia K. Hall, Friez Gordon, Beaumont Construction Attorneys, and Douglas Greer Law Office among them. The rooms were adapted for function; their character, quietly preserved.",
    ],
  },
  {
    year: "2025",
    heading: "A restoration, a tea room.",
    body: [
      "Matthew and Kimberly Moyle — the latter a chef — took possession in April of 2025 and set about bringing the house back into full hospitality. Plumbing was renewed and nearly the entire sewer line replaced. Wiring was brought up to code; every bathroom refitted. The front of the house was leveled (the back is to follow), and the original floors resurfaced — the dining-room floor had to be lifted and rebuilt to accommodate the work.",
      "During the restoration a long-boarded bathroom window was uncovered, and a partition wall that had split the garden room in two was removed. Where the garden-room floor could not be saved, Kim laid it in gold epoxy — the same treatment carried into the main-floor bath. The living-room fireplace was reclad in European marble, echoed through the bathrooms upstairs.",
      "The house had been without hot water for years; new gas lines and a tankless system restored it. The old kitchen was taken out and replaced with a commercial kitchen — hood system, grease trap, and all — ready to serve. As much of the original charm as could be kept was kept.",
    ],
  },
];

const About: React.FC = () => {
  return (
    <div className="pt-28 pb-24">
      <motion.header
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: easeManor }}
        className="max-w-3xl mx-auto text-center px-6"
      >
        <p className="u-label">Our Story</p>
        <h1 className="mt-4 font-display text-[clamp(2.8rem,7vw,5.25rem)] leading-[0.98]">
          A century of <em>hospitality</em>,
          <br />
          kept in one house.
        </h1>
        <div className="mt-6 flex justify-center">
          <span className="u-script" style={{ fontSize: "2.6rem" }}>
            since nineteen twenty-six
          </span>
        </div>
        <div className="mt-8">
          <Divider />
        </div>
      </motion.header>

      <motion.figure
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.1, ease: easeManor }}
        className="mx-auto mt-16 max-w-4xl px-6"
      >
        <div className="frame-gilt">
          <img
            src={house}
            alt="Moyle Manor"
            className="w-full h-[360px] md:h-[480px] object-cover"
          />
        </div>
        <figcaption className="mt-3 text-center u-label">
          Moyle Manor · Beaumont
        </figcaption>
      </motion.figure>

      <div className="max-w-3xl mx-auto mt-20 px-6 space-y-24">
        {chapters.map((c, idx) => (
          <motion.article
            key={c.year}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: easeManor }}
            className="relative"
          >
            <div className="flex items-baseline gap-6">
              <span
                className="font-display text-[color:var(--gilt-deep)] whitespace-nowrap"
                style={{
                  fontSize: "clamp(2.6rem, 6vw, 4.25rem)",
                  lineHeight: 1,
                }}
              >
                {c.year}
              </span>
              <span className="h-px flex-1 bg-[color:var(--gilt-hairline)]" />
            </div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl text-[color:var(--claret-deep)]">
              {c.heading}
            </h3>
            {c.body.map((para, pi) => (
              <p
                key={`${c.year}-${para.slice(0, 24)}`}
                className={`mt-5 text-lg leading-[1.8] text-[color:var(--ink-soft)] ${
                  idx === 0 && pi === 0 ? "u-dropcap" : ""
                }`}
              >
                {para}
              </p>
            ))}
            {c.aside}
            {idx < chapters.length - 1 && (
              <div className="mt-16 flex justify-center text-[color:var(--gilt-deep)]">
                <Fleuron className="w-12 h-3" />
              </div>
            )}
          </motion.article>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: easeManor }}
        className="max-w-3xl mx-auto mt-24 px-6 text-center"
      >
        <Divider />
        <p
          className="mt-8 u-script text-[color:var(--claret)]"
          style={{ fontSize: "2.8rem" }}
        >
          welcome home
        </p>
        <p className="mt-4 u-label">Matthew &amp; Kimberly Moyle</p>
        <p className="mt-10 text-sm italic text-[color:var(--ink-soft)]">
          This website — a quiet offering to the manor — is maintained by their
          son, <span className="text-[color:var(--claret)]">Taylor Moyle</span>.
        </p>
      </motion.div>
    </div>
  );
};

export default About;
